import React, { useState } from 'react';
import { Lock, ShieldAlert, CreditCard, ArrowRight, Loader2, UploadCloud } from 'lucide-react';
import Swal from 'sweetalert2';
import type { PaymentInfo } from '../../utils/paymentUtils';

interface AccessRevokedOverlayProps {
    paymentInfo: PaymentInfo;
    children?: React.ReactNode;
    title?: string;
    subtitle?: string;
    compact?: boolean;
}

export const AccessRevokedOverlay: React.FC<AccessRevokedOverlayProps> = ({ 
    paymentInfo, 
    children, 
    title, 
    subtitle, 
    compact = false 
}) => {
    const [isProcessing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    if (paymentInfo.isFullyPaid || !paymentInfo.isExpired) {
        return <>{children}</>;
    }

    const handlePayBalance = async () => {
        // Direct Square payment link (temporary fallback)
        const directSquareLink = paymentInfo?.paymentLink || 'https://square.link/u/HjJ7Mn7U';
        window.open(directSquareLink, '_blank');
    };

    const handleUploadReceipt = async () => {
        const { value: file } = await Swal.fire({
            title: 'Upload Proof of Payment',
            text: 'Please select your payment receipt, Square receipt, or transfer screenshot (JPEG, PNG, PDF up to 10MB).',
            input: 'file',
            inputAttributes: {
                accept: 'image/*,application/pdf',
                'aria-label': 'Upload payment proof'
            },
            showCancelButton: true,
            confirmButtonText: 'Submit Proof',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'var(--index-primary-color, #0F5132)'
        });

        if (!file) return;

        setIsUploading(true);
        const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const formData = new FormData();
        formData.append('receipt', file);

        try {
            const response = await fetch(`${API_URL}/student/upload-receipt`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            const data = await response.json();
            if (response.ok && data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Proof Submitted!',
                    text: data.message || 'Proof of payment uploaded successfully. The admissions team has been notified and will verify your balance settlement shortly.',
                    confirmButtonColor: 'var(--index-primary-color, #0F5132)'
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload Failed',
                    text: data.message || 'Could not process receipt upload. Please check the file format and try again.',
                    confirmButtonColor: '#dc2626'
                });
            }
        } catch (err: any) {
            Swal.fire({
                icon: 'error',
                title: 'Network Error',
                text: err.message || 'Error communicating with server.',
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setIsUploading(false);
        }
    };

    const defaultTitle = 'Access Revoked Due to Expired Payment';
    const defaultSubtitle = `Your payment deadline (${paymentInfo.deadlineDisplay || 'Monday, September 21, 2026 at 11:59 PM CST'}) for ${paymentInfo.courseTitle || 'your track'} has expired. Lessons, videos, live sessions, and course materials are locked until your remaining tuition balance of $${paymentInfo.remainingBalance} is settled.`;

    if (compact) {
        return (
            <div className="relative w-full rounded-2xl overflow-hidden bg-brand-charcoal text-white p-5 sm:p-6 text-center border-2 border-red-500/40 shadow-xl animate-in fade-in duration-300">
                <div className="absolute inset-0 bg-gradient-to-b from-red-950/40 via-brand-charcoal to-brand-charcoal pointer-events-none" />

                <div className="relative z-10 space-y-4">
                    <div className="w-12 h-12 bg-red-500/20 text-red-500 rounded-2xl flex items-center justify-center mx-auto border border-red-500/30 shadow-inner">
                        <Lock size={22} />
                    </div>

                    <div className="space-y-1.5">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-full text-[9px] font-black uppercase tracking-widest">
                            <ShieldAlert size={10} /> Access Suspended
                        </div>
                        <h4 className="text-base sm:text-lg font-black tracking-tight">{title || 'Live Classes Locked'}</h4>
                        <p className="text-xs font-medium text-white/70 leading-relaxed">
                            {subtitle || `Your 50% installment deadline expired on ${paymentInfo.deadlineDisplay}. Settle your remaining balance of $${paymentInfo.remainingBalance} to unlock upcoming live classes.`}
                        </p>
                    </div>

                    <div className="p-3 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between text-xs font-bold text-white/80">
                        <span>Balance Due:</span>
                        <span className="text-base font-black text-red-400">${paymentInfo.remainingBalance}</span>
                    </div>

                    <div className="pt-1 flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={handlePayBalance}
                            disabled={isProcessing}
                            className="w-full h-11 bg-brand-emerald text-white rounded-xl font-black text-[11px] uppercase tracking-wider shadow-lg shadow-brand-emerald/30 hover:brightness-110 active:scale-95 transition-all border-none cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <CreditCard size={15} /> Pay Balance (${paymentInfo.remainingBalance}) <ArrowRight size={14} />
                        </button>

                        <button
                            type="button"
                            onClick={handleUploadReceipt}
                            disabled={isUploading}
                            className="w-full h-10 px-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl font-black text-[10px] uppercase tracking-wider active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isUploading ? (
                                <><Loader2 size={14} className="animate-spin" /> Submitting...</>
                            ) : (
                                <><UploadCloud size={14} /> Upload Proof</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-[380px] w-full rounded-3xl overflow-hidden bg-brand-charcoal text-white flex items-center justify-center p-6 sm:p-12 text-center border-2 border-red-500/40 shadow-2xl animate-in fade-in duration-300">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-b from-red-950/40 via-brand-charcoal to-brand-charcoal pointer-events-none" />

            <div className="relative z-10 max-w-xl space-y-6">
                <div className="w-20 h-20 bg-red-500/20 text-red-500 rounded-3xl flex items-center justify-center mx-auto border border-red-500/30 shadow-inner">
                    <Lock size={40} />
                </div>

                <div className="space-y-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 text-red-400 border border-red-500/20 rounded-full text-[10px] font-black uppercase tracking-widest">
                        <ShieldAlert size={12} /> Access Suspended
                    </div>
                    <h2 className="text-2xl sm:text-4xl font-black tracking-tight">{title || defaultTitle}</h2>
                    <p className="text-sm font-medium text-white/70 leading-relaxed">
                        {subtitle || defaultSubtitle}
                    </p>
                </div>

                <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-between text-xs font-bold text-white/80">
                    <span>Remaining Balance Due:</span>
                    <span className="text-xl font-black text-red-400">${paymentInfo.remainingBalance}</span>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={handlePayBalance}
                        disabled={isProcessing}
                        className="flex-1 h-14 bg-brand-emerald text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-brand-emerald/30 hover:scale-[1.02] active:scale-95 transition-all border-none cursor-pointer flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {isProcessing ? (
                            <><Loader2 size={18} className="animate-spin" /> Connecting Square...</>
                        ) : (
                            <><CreditCard size={18} /> Pay Balance (${paymentInfo.remainingBalance}) <ArrowRight size={16} /></>
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={handleUploadReceipt}
                        disabled={isUploading}
                        className="h-14 px-6 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isUploading ? (
                            <><Loader2 size={16} className="animate-spin" /> Submitting...</>
                        ) : (
                            <><UploadCloud size={16} /> Upload Proof</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

